import type { InvoiceDto, UpcomingInvoiceDto } from "../../services/BillingService";
import type { Invoice, InvoiceStatus, UpcomingInvoice } from "../../types/Invoice";
import { isNil } from "lodash-es";

const toInvoiceStatus = (status: string): InvoiceStatus => {
  switch (status) {
    case "OPEN":
    case "PROCESSING":
    case "DRAFT":
    case "PAID":
    case "UNCOLLECTIBLE":
    case "VOID":
      return status;
    default:
      return "UNKNOWN";
  }
};

export const toInvoice = (dto: InvoiceDto): Invoice => ({
  invoiceNumber: dto.invoiceNumber,
  invoiceDate: new Date(dto.invoiceDate),
  invoiceStatus: toInvoiceStatus(dto.invoiceStatus),
  statusUrl: dto.statusUrl,
  pdfUrl: dto.pdfUrl,
  paymentStatus: {
    currency: dto.paymentStatus.currency,
    totalInCents: dto.paymentStatus.total,
  },
});

const resolveUpcomingInvoiceDiscount = (discount: UpcomingInvoiceDto["discount"]) => {
  if (isNil(discount)) {
    return;
  }

  const { amountOff, percentOff } = discount.coupon;
  if (!isNil(percentOff)) {
    return { coupon: { type: "PERCENTAGE" as const, amount: percentOff } };
  }
  if (!isNil(amountOff)) {
    return { coupon: { type: "FIXED_AMOUNT" as const, amount: amountOff } };
  }
};

export const toUpcomingInvoice = (dto: UpcomingInvoiceDto): UpcomingInvoice => ({
  invoiceDate: new Date(dto.invoiceDate * 1000),
  invoiceLines: dto.invoiceLines.map(({ description, amount }) => ({
    description,
    amount,
  })),
  discount: resolveUpcomingInvoiceDiscount(dto.discount),
  subtotal: dto.subTotal,
  tax: dto.tax,
  taxPercentage: dto.taxPercent ?? 0,
  total: dto.total,
  amountDue: dto.amountDue,
  startingBalance: dto.startingBalance ?? 0,
  currency: dto.currency,
});
